import {useMemo} from 'react';
import {isEqual} from 'lodash';

import {ResolveName, StoredFilter, UseFilterNameResolver, UseStoredFilters} from 'filters/features/types';
import {StoredPolicyFilter, PolicyFilter} from 'modules/decisionsNext/api/types';
import {PolicyPageFilter} from 'filters/types';

import {DEFAULT_FILTER_NAME} from 'core/constants/policyFilter';
import {useFormatGlobalFilter} from 'features/GlobalFilterProvider/hooks/useFormatGlobalFilter';
import {savedFilterPresets} from 'features/GlobalFilterProvider/constants';
import {
  SavedPolicyFiltersQuery,
  useRecentPolicyFiltersQuery,
  useSavedPolicyFiltersQuery,
} from 'features/GlobalFilterProvider/api/policyFilters.generated';
import {PolicyFilterTag} from 'constants/policyFilterTags';
import {usePolicyFilterNameResolver} from 'features/GlobalFilterProvider/hooks/usePolicyFilterNameResolver';

const formatFilters = (
  filters: StoredPolicyFilter[] | undefined,
  formatter: ReturnType<typeof useFormatGlobalFilter>
): StoredFilter[] | undefined => {
  return filters?.map(stored => ({
    id: stored.id,
    name: stored.name,
    lastUsed: stored.lastUsed as string,
    filter: {
      ...formatter(stored.filter),
      _type: 'policy' as const,
    },
  }));
};

const formatSavedFilters = (
  formatter: ReturnType<typeof useFormatGlobalFilter>,
  saved?: SavedPolicyFiltersQuery['storedFiltersV2']['policyFiltersList'],
  activeFilterId?: string,
  withPresets?: boolean
) => {
  const formattedSaved = formatFilters(saved, formatter);

  const defaultStoredFilter = formattedSaved?.find(filter => filter.name === DEFAULT_FILTER_NAME);
  const activeFilter = formattedSaved?.find(f => f.id === activeFilterId);
  const withoutDuplicates = (
    defaultStoredFilter?.id === activeFilterId ? [defaultStoredFilter] : [defaultStoredFilter, activeFilter]
  ).filter(Boolean);

  const duplicates = formattedSaved?.filter(storedFilter => {
    if ([activeFilterId, defaultStoredFilter?.id].includes(storedFilter.id)) {
      return false;
    }

    if (!withoutDuplicates.find(stored => isEqual(stored.filter, storedFilter.filter))) {
      withoutDuplicates.push(storedFilter);
      return false;
    }

    return true;
  });

  const withoutDuplicatesMap = withoutDuplicates.reduce<Record<string, StoredFilter>>((acc, storedFilter) => {
    acc[storedFilter.id] = storedFilter;
    return acc;
  }, {});

  return {
    presets: withPresets
      ? savedFilterPresets.map(preset => ({
          ...preset,
          filter: {
            ...formatter(preset.filter),
            _type: 'policy' as const,
          },
        }))
      : undefined,
    savedWithDefault: withoutDuplicates,
    duplicates,
    defaultFilter: defaultStoredFilter?.filter || {_type: 'policy'},
    saved: formattedSaved?.filter(filter => filter.name !== DEFAULT_FILTER_NAME && withoutDuplicatesMap[filter.id]),
  };
};

const formatRecentFilters = (
  recent?: StoredFilter[],
  saved?: StoredFilter[],
  resolveName?: ResolveName<PolicyPageFilter>
) => {
  return recent?.map(storedRecent => {
    const foundSaved = saved?.find(storedSaved => isEqual(storedSaved.filter, storedRecent.filter));

    if (foundSaved) {
      return {
        ...foundSaved,
        lastUsed: storedRecent.lastUsed,
      };
    }

    return {
      ...storedRecent,
      name:
        resolveName?.(storedRecent.filter as PolicyPageFilter)
          .map(part => part.getValue())
          .filter(Boolean)
          .join(', ') || '<Unnamed Filter>',
    };
  });
};

type CreateUsePolicyStoredFiltersParams = {
  tag: PolicyFilterTag;
  useFormatter: () => (filter: PolicyFilter) => PolicyFilter;
  useFilterNameResolver: UseFilterNameResolver<PolicyPageFilter>;
  withPresets?: boolean;
  withRecent?: boolean;
};

export const createUsePolicyStoredFilters: (params: CreateUsePolicyStoredFiltersParams) => UseStoredFilters =
  ({tag, useFormatter, useFilterNameResolver, withPresets, withRecent = true}) =>
  () => {
    const {data: recentData, loading: recentLoading} = useRecentPolicyFiltersQuery({
      variables: {
        tag,
      },
      skip: !withRecent,
      disableSkipSetsLoadingState: true,
    });
    const {data: savedData, loading: savedLoading} = useSavedPolicyFiltersQuery({
      variables: {
        tag,
      },
    });
    const formatFilter = useFormatter();
    const {resolverLoading, resolveName} = useFilterNameResolver();

    const activeFilterId = recentData?.storedFiltersV2.policyFiltersList?.[0]?.id;

    const savedFilters = useMemo(() => {
      return formatSavedFilters(
        formatFilter,
        savedData?.storedFiltersV2.policyFiltersList,
        activeFilterId,
        withPresets
      );
    }, [formatFilter, savedData?.storedFiltersV2.policyFiltersList, activeFilterId]);

    const recentFilters = useMemo(() => {
      if (!withRecent) {
        return null;
      }

      return formatRecentFilters(
        formatFilters(recentData?.storedFiltersV2.policyFiltersList, formatFilter),
        savedFilters.savedWithDefault,
        resolveName
      );
    }, [formatFilter, recentData?.storedFiltersV2.policyFiltersList, resolveName, savedFilters.savedWithDefault]);

    return {
      savedLoading,
      savedFilters,
      recentLoading: recentLoading || resolverLoading,
      recentFilters,
    };
  };

export const useGlobalStoredFilters = createUsePolicyStoredFilters({
  tag: PolicyFilterTag.GLOBAL,
  useFormatter: useFormatGlobalFilter,
  useFilterNameResolver: usePolicyFilterNameResolver,
  // Keep it true only for global filters, we would refactor later to remove presets at all
  withPresets: true,
});
